"use client"

import * as React from "react"
import { usePathname } from "next/navigation"
import { ThemeProvider as NextThemesProvider } from "next-themes"
import { isAdminConsolePath } from "@/lib/admin/theme/admin-ui"
import { adminModeSnapshot, subscribeAdminMode } from "@/lib/admin/theme/admin-mode-store"

const noSubscription = () => () => {}
const noMode = (): undefined => undefined

/**
 * ThemeProvider - Wraps the application with next-themes for light/dark mode
 * Color themes are managed by ThemeSwitcher via localStorage + body classes
 *
 * The admin console (/admin, its sign-in pages included) keeps its own light / dark / system,
 * saved with the admin (docs/superpowers/specs/2026-09-27-admin-console-themes-design.md, "Light
 * and dark, the console's own"). next-themes 0.4.6 reads its storage key once, on mount, and
 * re-applies its class in an effect that runs after the page's own effects, so the console could
 * not own the class any other way: while a console page is open this passes `forcedTheme` — the
 * mode in the hf-admin-ui cookie, the device's while the admin chose System — and updates it when
 * the console says the choice changed. "Open": under /admin, with a console layout's
 * `data-admin-theme` on <html> (lib/admin/theme/admin-mode-store.ts), so an /admin URL no console
 * layout renders (a not-found) is left alone. During server rendering and hydration it is
 * unknown; the console's first-paint script has already set the class.
 *
 * Whenever nothing is forced — every other path (the customer app, the play area, the learner
 * portal) and an /admin URL that is not a console page — next-themes gets exactly the props it got
 * before (components/__tests__/theme-provider-admin-branch.spec.tsx).
 */
export function ThemeProvider({
  children,
  ...props
}: React.ComponentProps<typeof NextThemesProvider>) {
  const inConsole = isAdminConsolePath(usePathname())
  const adminMode = React.useSyncExternalStore(
    inConsole ? subscribeAdminMode : noSubscription,
    inConsole ? adminModeSnapshot : noMode,
    noMode,
  )

  if (adminMode === undefined) {
    return (
      <NextThemesProvider {...props}>
        {children}
      </NextThemesProvider>
    )
  }
  return (
    <NextThemesProvider {...props} forcedTheme={adminMode}>
      {children}
    </NextThemesProvider>
  )
}
